import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated } from '../../lib/api';
import { SAUDI_CITIES } from '../../lib/saudi-locations';
import { LocationPicker } from '../../components/LocationPicker';

interface PropertyRow {
  id: string;
  type: string;
  purpose: string;
  title: string;
  description: string | null;
  price: number | string;
  area: number;
  rooms: number;
  bathrooms: number;
  city: string;
  district: string | null;
  coverImage: string | null;
  featured: boolean;
  status: string;
  ownerId: string | null;
  owner?: { id: string; name: string; phone: string | null; email: string | null } | null;
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: 'green' | 'amber' | 'navy' | 'red' | 'gray'; icon: string }> = {
  ACTIVE:              { label: 'نشط',                  color: 'green', icon: '✓' },
  PENDING_LICENSE:     { label: 'بانتظار رخصة الإعلان', color: 'amber', icon: '📋' },
  PENDING_REVIEW:      { label: 'قيد المراجعة',          color: 'amber', icon: '⏳' },
  PENDING:             { label: 'قيد المراجعة',          color: 'amber', icon: '⏳' },
  DRAFT:               { label: 'مسودة',                color: 'gray',  icon: '📝' },
  SOLD:                { label: 'تم البيع',             color: 'navy',  icon: '💰' },
  RENTED:              { label: 'تم التأجير',           color: 'navy',  icon: '🏠' },
  REJECTED:            { label: 'مرفوض',                color: 'red',   icon: '✕' },
  REMOVED_UNLICENSED:  { label: 'مُزال — بدون رخصة',    color: 'red',   icon: '🛑' },
  ARCHIVED:            { label: 'مؤرشف',                color: 'gray',  icon: '🗄️' },
};

const TYPES: Record<string, string> = {
  VILLA: 'فيلا', APARTMENT: 'شقة', LAND: 'أرض', COMMERCIAL: 'تجاري', OFFICE: 'مكتب',
};

export function AdminPropertiesPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', city: '', type: '', purpose: '', q: '', page: 1, limit: 20 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [openLicenseId, setOpenLicenseId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'properties', filters],
    queryFn: () => api.get<Paginated<PropertyRow>>('/api/properties/admin', { query: filters }),
    placeholderData: keepPreviousData, // 🚀 يبقي البيانات القديمة معروضة حتى تطلع الجديدة
    staleTime: 30_000,                  // 🚀 لو رجعت لنفس الفلتر خلال ٣٠ ثانية يطلع فوراً
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'properties'] });

  const updateStatus = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      api.put(`/api/properties/${id}/status`, { status, rejectionReason: reason }),
    onSuccess: () => { invalidate(); setRejectId(null); setRejectReason(''); },
  });

  const toggleFeatured = useMutation({
    mutationFn: ({ id, featured }: { id: string; featured: boolean }) =>
      api.put(`/api/properties/${id}/featured`, { featured }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/properties/${id}`),
    onSuccess: invalidate,
  });

  // 📊 إحصاءات الحالات
  const list = data?.data ?? [];
  const counts: Record<string, number> = {};
  list.forEach((p) => { counts[p.status] = (counts[p.status] ?? 0) + 1; });

  return (
    <div>
      {/* 🎯 رأس الصفحة */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">🏘️ إدارة العقارات</h1>
          <p className="text-sm text-ink-muted mt-1">مراجعة + اعتماد + تمييز + إدارة كاملة لعقارات المنصة</p>
        </div>
        <Link to="/submit-property">
          <Button variant="primary">➕ إضافة عقار جديد</Button>
        </Link>
      </div>

      {/* 🔄 شريط تحميل خفيف لما الفلتر يتغير ولسا البيانات جاية */}
      {isFetching && !isLoading && (
        <div className="h-0.5 bg-gold/20 overflow-hidden mb-2 rounded-full">
          <div className="h-full bg-gold animate-pulse" style={{ width: '40%' }} />
        </div>
      )}

      {/* 📊 شارات سريعة (تعتمد على الصفحة الحالية فقط) */}
      <div className={`grid grid-cols-2 md:grid-cols-6 gap-2 mb-6 transition-opacity ${isFetching && !isLoading ? 'opacity-70' : 'opacity-100'}`}>
        <QuickStat label="الإجمالي"        count={data?.pagination?.total ?? 0} color="navy"  icon="🏘️"
          active={!filters.status} onClick={() => setFilters({ ...filters, status: '', page: 1 })} />
        <QuickStat label="بانتظار رخصة"   count={counts['PENDING_LICENSE'] ?? 0} color="amber" icon="📋"
          active={filters.status === 'PENDING_LICENSE'} onClick={() => setFilters({ ...filters, status: filters.status === 'PENDING_LICENSE' ? '' : 'PENDING_LICENSE', page: 1 })} pulse />
        <QuickStat label="قيد المراجعة"   count={counts['PENDING_REVIEW'] ?? counts['PENDING'] ?? 0} color="amber" icon="⏳"
          active={filters.status === 'PENDING_REVIEW'} onClick={() => setFilters({ ...filters, status: filters.status === 'PENDING_REVIEW' ? '' : 'PENDING_REVIEW', page: 1 })} pulse />
        <QuickStat label="نشط"             count={counts['ACTIVE'] ?? 0} color="green" icon="✓"
          active={filters.status === 'ACTIVE'} onClick={() => setFilters({ ...filters, status: filters.status === 'ACTIVE' ? '' : 'ACTIVE', page: 1 })} />
        <QuickStat label="مرفوض"           count={counts['REJECTED'] ?? 0} color="red"   icon="✕"
          active={filters.status === 'REJECTED'} onClick={() => setFilters({ ...filters, status: filters.status === 'REJECTED' ? '' : 'REJECTED', page: 1 })} />
        <QuickStat label="مباع/مؤجّر"     count={(counts['SOLD'] ?? 0) + (counts['RENTED'] ?? 0)} color="gray" icon="💰"
          active={filters.status === 'SOLD' || filters.status === 'RENTED'} onClick={() => setFilters({ ...filters, status: filters.status === 'SOLD' ? '' : 'SOLD', page: 1 })} />
      </div>

      {/* 🔍 الفلاتر */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Input
                placeholder="🔍 بحث في العنوان/الوصف…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
              />
            </div>
            <select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل المدن</option>
              {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">كل الأنواع</option>
              {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select
              value={filters.purpose}
              onChange={(e) => setFilters({ ...filters, purpose: e.target.value, page: 1 })}
              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
            >
              <option value="">للبيع والإيجار</option>
              <option value="SALE">للبيع</option>
              <option value="RENT">للإيجار</option>
              <option value="INVESTMENT">للاستثمار</option>
            </select>
          </div>
        </CardBody>
      </Card>

      {/* 📋 الجدول */}
      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>العقار</Th>
                <Th>النوع</Th>
                <Th>المدينة</Th>
                <Th>السعر</Th>
                <Th>الحالة</Th>
                <Th>مميّز</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !list.length ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-2">🏚️</div>
                  لا توجد عقارات تطابق الفلاتر
                </td></tr>
              ) : list.map((p) => (
                <tr key={p.id} className={`border-b border-gray-100 hover:bg-gray-50 transition ${p.status === 'PENDING_REVIEW' || p.status === 'PENDING' ? 'bg-amber-50/30' : ''}`}>
                  <Td>
                    <div className="flex items-center gap-3 min-w-0">
                      {p.coverImage ? (
                        <img src={p.coverImage} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-navy-soft flex items-center justify-center text-xl shrink-0">🏠</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-navy-dark line-clamp-1">{p.title}</div>
                        <div className="text-[10px] text-ink-muted">📐 {p.area} م² · 🛏 {p.rooms} · 🚿 {p.bathrooms}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <div className="text-xs">{TYPES[p.type] ?? p.type}</div>
                    <div className="text-[10px] text-ink-muted">{p.purpose === 'SALE' ? 'للبيع' : 'للإيجار'}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{p.city}</div>
                    {p.district && <div className="text-[10px] text-ink-muted">{p.district}</div>}
                  </Td>
                  <Td className="font-bold">{Number(p.price).toLocaleString('en-US')} <span className="text-[10px] text-ink-muted">ر.س</span></Td>
                  <Td>
                    <Badge color={STATUS_LABELS[p.status]?.color ?? 'gray'}>
                      {STATUS_LABELS[p.status]?.icon} {STATUS_LABELS[p.status]?.label ?? p.status}
                    </Badge>
                  </Td>
                  <Td>
                    <button
                      onClick={() => toggleFeatured.mutate({ id: p.id, featured: !p.featured })}
                      disabled={toggleFeatured.isPending}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center transition ${
                        p.featured ? 'bg-gold text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-gold/20'
                      }`}
                      title={p.featured ? 'إلغاء التمييز' : 'تمييز'}
                    >
                      ⭐
                    </button>
                  </Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap">
                      {p.status === 'PENDING_LICENSE' && (
                        <button
                          onClick={() => setOpenLicenseId(p.id)}
                          className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11px] font-bold border border-amber-200 transition"
                        >
                          📋 إصدار رخصة
                        </button>
                      )}
                      {(p.status === 'PENDING_REVIEW' || p.status === 'PENDING') && (
                        <>
                          <button
                            onClick={() => updateStatus.mutate({ id: p.id, status: 'ACTIVE' })}
                            disabled={updateStatus.isPending}
                            className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 text-[11px] font-bold border border-green-200 transition"
                          >
                            ✓ اعتماد
                          </button>
                          <button
                            onClick={() => { setRejectId(p.id); setRejectReason(''); }}
                            className="px-2.5 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-[11px] font-bold border border-red-200 transition"
                          >
                            ✕ رفض
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setPreviewId(p.id)}
                        className="px-2.5 py-1 rounded-lg bg-navy-soft text-navy-dark hover:bg-navy/10 text-[11px] font-bold border border-navy/20 transition"
                      >
                        👁️ عرض ومراجعة
                      </button>
                      <button
                        onClick={() => setOpenId(p.id)}
                        className="px-2.5 py-1 rounded-lg bg-gray-50 text-ink hover:bg-gray-100 text-[11px] font-bold border border-gray-200 transition"
                      >
                        ⚙️ تحكم
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`حذف "${p.title}"؟ لا يمكن التراجع.`)) deleteMutation.mutate(p.id);
                        }}
                        className="px-2 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-[11px] font-bold border border-red-200 transition"
                        title="حذف"
                      >
                        🗑️
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>السابق</Button>
          <span className="px-4 py-2 text-sm font-bold">{filters.page} / {data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>التالي</Button>
        </div>
      )}

      {/* 🚫 مودال الرفض */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setRejectId(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-black text-lg text-navy-dark mb-2">✕ رفض العقار</h3>
            <p className="text-xs text-ink-muted mb-3">سبب الرفض سيظهر للمالك ليعدّل العقار.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="مثل: الصور غير واضحة، السعر غير منطقي، البيانات ناقصة…"
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm resize-y"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setRejectId(null)}>إلغاء</Button>
              <Button
                variant="primary"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={() => updateStatus.mutate({ id: rejectId, status: 'REJECTED', reason: rejectReason })}
                loading={updateStatus.isPending}
                disabled={rejectReason.trim().length < 5}
              >
                تأكيد الرفض
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ⚙️ مودال التحكم */}
      {openId && (
        <PropertyControlModal
          property={list.find((p) => p.id === openId)!}
          onClose={() => setOpenId(null)}
          onUpdated={invalidate}
        />
      )}

      {/* 👁️ مودال المراجعة الشاملة (preview) */}
      {previewId && (
        <PropertyReviewModal
          propertyId={previewId}
          onClose={() => setPreviewId(null)}
          onUpdated={invalidate}
          onReject={() => { setRejectId(previewId); setPreviewId(null); }}
        />
      )}

      {/* 📋 مودال إصدار رخصة الإعلان (SREM) */}
      {openLicenseId && (
        <LicenseIssueModal
          property={list.find((p) => p.id === openLicenseId)!}
          onClose={() => setOpenLicenseId(null)}
          onIssued={invalidate}
        />
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال إصدار رخصة الإعلان (SREM) ━━━━━━━━━━━━━━━━━━━━

function LicenseIssueModal({ property, onClose, onIssued }: { property: PropertyRow; onClose: () => void; onIssued: () => void }) {
  const [tab, setTab] = useState<'auto' | 'manual'>('auto');
  const [manual, setManual] = useState({ licenseNumber: '', expiresAt: '', issuer: 'BORSA' as 'BORSA' | 'REGA' | 'OTHER' });
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');

  const autoM = useMutation({
    mutationFn: () => api.post<{ data: { adLicenseNumber: string } }>(`/api/properties/admin/${property.id}/request-license`, {}),
    onSuccess: (r) => {
      setSuccess(`✅ صدرت الرخصة: ${r.data.adLicenseNumber}`);
      setTimeout(() => { onIssued(); onClose(); }, 1500);
    },
    onError: (e: Error) => setErr(e.message),
  });

  const manualM = useMutation({
    mutationFn: () => api.post(`/api/properties/admin/${property.id}/manual-license`, {
      licenseNumber: manual.licenseNumber.trim(),
      expiresAt: manual.expiresAt || null,
      issuer: manual.issuer,
      activate: true,
    }),
    onSuccess: () => { setSuccess('✅ تم تسجيل الرخصة وتفعيل الإعلان'); setTimeout(() => { onIssued(); onClose(); }, 1200); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b">
          <h2 className="text-xl font-black text-navy-dark">📋 إصدار رخصة إعلان</h2>
          <p className="text-xs text-gray-500 mt-1">{property.title}</p>
        </div>

        <div className="px-5 pt-3 flex gap-2 border-b">
          <button onClick={() => setTab('auto')} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition ${tab === 'auto' ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500'}`}>🚀 طلب تلقائي من SREM</button>
          <button onClick={() => setTab('manual')} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition ${tab === 'manual' ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500'}`}>✏️ إدخال يدوي</button>
        </div>

        <div className="p-5 space-y-3">
          {tab === 'auto' ? (
            <>
              <div className="bg-navy-soft/30 border border-navy/20 rounded-lg p-4 text-sm text-navy-dark leading-relaxed">
                <b>🚀 الطلب التلقائي:</b> سنرسل بيانات العقار + المُعلِن لـ البورصة العقارية (SREM) عبر API.
                إذا قبلت SREM البيانات، يصدر رقم رخصة فوراً ويتفعّل الإعلان تلقائياً.
              </div>
              <ul className="text-xs text-gray-600 space-y-1 list-disc pr-5">
                <li>المُعلِن لازم يكون متحقّق بنفاذ ✓</li>
                <li>رقم الصك مطلوب ✓</li>
                <li>لو لم تكن شهادة الـ API متاحة، سيرجع لك خطأ "Sandbox" — استخدم الإدخال اليدوي</li>
              </ul>
              {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
              {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">{success}</div>}
              <Button variant="primary" className="w-full" onClick={() => autoM.mutate()} loading={autoM.isPending}>🚀 طلب الرخصة الآن</Button>
            </>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                💡 ادخل على <a href="https://srem.rega.gov.sa" target="_blank" rel="noreferrer" className="underline font-bold">srem.rega.gov.sa</a>، أصدر الرخصة يدوياً، ثم انسخ الرقم هنا.
              </div>
              <Input
                label="رقم الرخصة *"
                value={manual.licenseNumber}
                onChange={(e) => setManual({ ...manual, licenseNumber: e.target.value })}
                placeholder="مثال: 7200000000"
                dir="ltr"
              />
              <Input
                label="تاريخ الانتهاء (اختياري)"
                type="date"
                value={manual.expiresAt}
                onChange={(e) => setManual({ ...manual, expiresAt: e.target.value })}
              />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">جهة الإصدار</label>
                <select value={manual.issuer} onChange={(e) => setManual({ ...manual, issuer: e.target.value as typeof manual.issuer })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                  <option value="BORSA">البورصة العقارية (SREM)</option>
                  <option value="REGA">الهيئة العامة للعقار</option>
                  <option value="OTHER">أخرى</option>
                </select>
              </div>
              {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
              {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">{success}</div>}
              <Button variant="primary" className="w-full" onClick={() => { if (manual.licenseNumber.trim().length < 4) { setErr('رقم الرخصة قصير'); return; } manualM.mutate(); }} loading={manualM.isPending}>💾 تسجيل الرخصة وتفعيل الإعلان</Button>
            </>
          )}
        </div>

        <div className="p-5 border-t flex justify-end">
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال المراجعة الشاملة ━━━━━━━━━━━━━━━━━━━━
//
// 🎯 خطوات المراجعة المنظّمة:
//   1. الاطلاع على البيانات الأساسية
//   2. التحقق من الصور
//   3. التحقق من الموقع على الخريطة
//   4. التحقق من الملكية والصك
//   5. القرار: اعتماد أو رفض
//
interface PropertyDetail extends PropertyRow {
  description: string | null;
  rooms: number;
  bathrooms: number;
  floors: number;
  ageYears: number;
  streetWidth: number;
  facade: string | null;
  district: string | null;
  address: string | null;
  latitude: number | string | null;
  longitude: number | string | null;
  features: string[] | null;
  images: string[] | null;
  videoUrl: string | null;
  virtualTourUrl: string | null;
  deedNumber: string | null;
  rejectionReason: string | null;
  publishedAt: string | null;
  // 📋 سجل المراجعة
  reviewedById: string | null;
  reviewedAt: string | null;
  reviewedBy?: { id: string; name: string; role: string } | null;
  reviewNotes: string | null;
  reviewChecklist: Record<string, boolean> | null;
  adLicenseNumber: string | null;
  adLicenseVerifiedAt: string | null;
}

function PropertyReviewModal({ propertyId, onClose, onUpdated, onReject }: {
  propertyId: string;
  onClose: () => void;
  onUpdated: () => void;
  onReject: () => void;
}) {
  // 📋 خطوات المراجعة الرسمية (موافقة الهيئة العامة للعقار)
  const [checks, setChecks] = useState({
    property:           false,  // 1️⃣ الاطلاع على العقار
    propertyDocs:       false,  // 2️⃣ التحقق من وثائق العقار (الصك)
    advertiserAndFal:   false,  // 3️⃣ التحقق من وثيقة المعلن ورخصة فال
    adLicense:          false,  // 4️⃣ التحقق من رخصة الإعلان العقاري ورقم الترخيص
  });
  const [activeImage, setActiveImage] = useState(0);
  const [adLicenseNum, setAdLicenseNum] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'property', propertyId],
    queryFn: () => api.get<{ data: PropertyDetail }>(`/api/properties/${propertyId}`),
  });

  const approve = useMutation({
    mutationFn: () => api.put(`/api/properties/${propertyId}/status`, {
      status: 'ACTIVE',
      adLicenseNumber: adLicenseNum.trim(),
      reviewNotes: reviewNotes.trim() || undefined,
      reviewChecklist: {
        property:           checks.property,
        propertyDocs:       checks.propertyDocs,
        advertiserAndFal:   checks.advertiserAndFal,
        adLicense:          checks.adLicense,
      },
    }),
    onSuccess: () => { onUpdated(); onClose(); },
  });

  if (isLoading || !data?.data) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 text-gray-500">⟳ جاري التحميل…</div>
      </div>
    );
  }

  const p = data.data;
  const allImages = [p.coverImage, ...(p.images ?? [])].filter(Boolean) as string[];
  const isPending = p.status === 'PENDING_REVIEW' || p.status === 'PENDING';
  const allChecked = checks.property && checks.propertyDocs && checks.advertiserAndFal && checks.adLicense;
  const checkedCount = Object.values(checks).filter(Boolean).length;
  const canApprove = allChecked && adLicenseNum.trim().length >= 5;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-2 md:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full mx-auto my-2" onClick={(e) => e.stopPropagation()}>
        {/* رأس */}
        <div className="bg-gradient-to-l from-navy-darker to-navy-dark text-white px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-black text-lg truncate">{p.title}</h2>
              <Badge color={STATUS_LABELS[p.status]?.color ?? 'gray'}>
                {STATUS_LABELS[p.status]?.icon} {STATUS_LABELS[p.status]?.label}
              </Badge>
            </div>
            <p className="text-xs text-white/70 mt-0.5">#{p.id.slice(-8).toUpperCase()} · {new Date(p.createdAt).toLocaleString('ar-SA')}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        {/* شريط تقدّم المراجعة */}
        {isPending && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs font-bold text-amber-900">📋 المراجعة: أكملت {checkedCount} من 4 خطوات</span>
              <div className="flex-1 h-1.5 bg-amber-100 rounded-full overflow-hidden max-w-xs">
                <div className="h-full bg-gradient-to-l from-gold-dark to-gold transition-all" style={{ width: `${(checkedCount / 4) * 100}%` }} />
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-5">
          {/* 🖼️ معرض الصور */}
          <section>
            <h3 className="text-sm font-black text-navy-dark mb-2 flex items-center gap-2 border-r-4 border-gold pr-2">
              🖼️ الصور ({allImages.length})
            </h3>
            {allImages.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
                ⚠️ لا توجد صور — قد يكون سبباً للرفض
              </div>
            ) : (
              <div>
                <div className="rounded-xl overflow-hidden bg-gray-100 mb-2 relative aspect-video max-h-96">
                  <img src={allImages[activeImage]} alt="" className="w-full h-full object-contain" />
                  {allImages.length > 1 && (
                    <>
                      <button onClick={() => setActiveImage((i) => (i - 1 + allImages.length) % allImages.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow text-lg">→</button>
                      <button onClick={() => setActiveImage((i) => (i + 1) % allImages.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow text-lg">←</button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full">
                        {activeImage + 1}/{allImages.length}
                      </div>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-6 md:grid-cols-10 gap-1.5">
                  {allImages.map((url, i) => (
                    <button key={i} onClick={() => setActiveImage(i)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 ${i === activeImage ? 'border-gold' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* 📝 البيانات الأساسية */}
          <section>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">📝 البيانات الأساسية</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ReviewField label="النوع" value={p.type} />
              <ReviewField label="الغرض" value={p.purpose === 'SALE' ? 'للبيع' : p.purpose === 'RENT' ? 'للإيجار' : p.purpose === 'INVESTMENT' ? 'للاستثمار' : p.purpose} />
              <ReviewField label="السعر" value={`${Number(p.price).toLocaleString('en-US')} ر.س`} highlight />
              <ReviewField label="المساحة" value={`${p.area} م²`} />
              <ReviewField label="غرف النوم" value={p.rooms?.toString() ?? '—'} />
              <ReviewField label="دورات المياه" value={p.bathrooms?.toString() ?? '—'} />
              <ReviewField label="الأدوار" value={p.floors?.toString() ?? '—'} />
              <ReviewField label="عمر العقار" value={p.ageYears ? `${p.ageYears} سنة` : '—'} />
              <ReviewField label="عرض الشارع" value={p.streetWidth ? `${p.streetWidth} م` : '—'} />
              <ReviewField label="الواجهة" value={p.facade ?? '—'} />
              <ReviewField label="رقم الصك" value={p.deedNumber ?? '—'} dir="ltr" />
            </div>
            {p.description && (
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3">
                <div className="text-[11px] font-bold text-ink-muted mb-1">📄 الوصف</div>
                <p className="text-sm whitespace-pre-line leading-relaxed">{p.description}</p>
              </div>
            )}
          </section>

          {/* 📍 الموقع */}
          <section>
            <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">📍 الموقع</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <ReviewField label="المدينة" value={p.city} />
              <ReviewField label="الحي" value={p.district ?? '—'} />
              <ReviewField label="الإحداثيات" value={p.latitude && p.longitude ? `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}` : '— لم يُحدّد'} dir="ltr" />
            </div>
            {p.address && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs">
                <span className="text-ink-muted">📌 العنوان التفصيلي:</span> {p.address}
              </div>
            )}
            {p.latitude && p.longitude && (
              <iframe
                title="خريطة العقار"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(p.longitude) - 0.005},${Number(p.latitude) - 0.003},${Number(p.longitude) + 0.005},${Number(p.latitude) + 0.003}&marker=${p.latitude},${p.longitude}`}
                className="w-full h-64 rounded-xl border border-gray-200 mt-2"
              />
            )}
          </section>

          {/* ✨ المزايا */}
          {p.features && p.features.length > 0 && (
            <section>
              <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">✨ المزايا</h3>
              <div className="flex flex-wrap gap-1.5">
                {p.features.map((f) => (
                  <span key={f} className="px-3 py-1 rounded-full bg-gold-soft text-gold-dark text-xs font-bold border border-gold/30">{f}</span>
                ))}
              </div>
            </section>
          )}

          {/* 👤 المالك */}
          {p.owner && (
            <section>
              <h3 className="text-sm font-black text-navy-dark mb-2 border-r-4 border-gold pr-2">👤 المالك</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-xl bg-navy-dark text-white flex items-center justify-center font-black">
                  {p.owner.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy-dark">{p.owner.name}</div>
                  {p.owner.phone && (
                    <a href={`tel:${p.owner.phone}`} className="text-xs text-navy hover:underline" dir="ltr">📞 {p.owner.phone}</a>
                  )}
                  {p.owner.email && (
                    <div className="text-xs text-ink-muted" dir="ltr">📧 {p.owner.email}</div>
                  )}
                </div>
                <Link to={`/admin/users?id=${p.owner.id}`} className="px-3 py-1.5 rounded-lg bg-navy-soft hover:bg-navy/10 text-xs font-bold text-navy-dark">👤 ملف المالك</Link>
              </div>
            </section>
          )}

          {/* ✅ خطوات المراجعة الرسمية (الهيئة العامة للعقار — REGA) */}
          {isPending && (
            <section className="bg-gradient-to-l from-amber-50 to-gold-soft border-2 border-gold/40 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-black text-gold-dark">📋 خطوات المراجعة الرسمية</h3>
                  <p className="text-[11px] text-ink-secondary mt-0.5">حسب اشتراطات الهيئة العامة للعقار (REGA) لنشر الإعلانات العقارية</p>
                </div>
                <Badge color="gold">أكملت {checkedCount} من 4 خطوات</Badge>
              </div>

              <div className="space-y-2">
                {/* 1️⃣ الاطلاع على العقار */}
                <ReviewCheckbox
                  step={1}
                  checked={checks.property}
                  onChange={(v) => setChecks((s) => ({ ...s, property: v }))}
                  label="الاطلاع على العقار"
                  hint="فحص بصري للصور، الوصف، الموقع، الأبعاد، السعر — التأكد من أن العقار حقيقي ومناسب للنشر"
                />

                {/* 2️⃣ التحقق من وثائق العقار (الصك) */}
                <ReviewCheckbox
                  step={2}
                  checked={checks.propertyDocs}
                  onChange={(v) => setChecks((s) => ({ ...s, propertyDocs: v }))}
                  label="التحقق من وثائق العقار"
                  hint={`صك الملكية ${p.deedNumber ? `(رقم الصك: ${p.deedNumber}) — تحقق من صلاحيته في وزارة العدل` : '— لم يُدخل رقم صك، اطلب من المعلن إرفاقه'}`}
                />

                {/* 3️⃣ التحقق من المعلن ورخصة فال */}
                <ReviewCheckbox
                  step={3}
                  checked={checks.advertiserAndFal}
                  onChange={(v) => setChecks((s) => ({ ...s, advertiserAndFal: v }))}
                  label="التحقق من المعلن ورخصة فال"
                  hint={`المعلن: ${p.owner?.name ?? '—'}. تأكّد من رخصة فال (الهيئة العامة للعقار) صالحة وغير منتهية، واسم المعلن مطابق لاسم صاحب الرخصة`}
                />

                {/* 4️⃣ التحقق من رخصة الإعلان العقاري — البورصة العقارية */}
                <div className={`p-2.5 rounded-xl border transition ${
                  checks.adLicense ? 'border-green-400 bg-green-50' : 'border-gold/30 bg-white'
                }`}>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checks.adLicense}
                      onChange={(e) => setChecks((s) => ({ ...s, adLicense: e.target.checked }))}
                      className="w-5 h-5 accent-green-600 mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-bold ${checks.adLicense ? 'text-green-800' : 'text-gold-dark'}`}>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold/20 text-[10px] font-black mr-1">4</span>
                        التحقق من رخصة الإعلان العقاري
                      </div>
                      <div className="text-[11px] text-ink-muted mt-0.5">
                        كل إعلان عقاري في السعودية يلزمه <b>رقم رخصة إعلان عقاري</b> يُصدَر من الهيئة العامة للعقار. تحقق من صلاحية الرقم وتطابق بياناته مع المعلن والعقار عبر
                        <a href="https://eservicesredp.rega.gov.sa/e-services/inquiry-about-the-real-estate-advertisement-license/form" target="_blank" rel="noreferrer" className="text-navy-dark underline mx-1">خدمة الاستعلام عن ترخيص إعلان</a>
                      </div>
                      <input
                        value={adLicenseNum}
                        onChange={(e) => setAdLicenseNum(e.target.value)}
                        placeholder="رقم رخصة الإعلان العقاري (من البورصة العقارية)"
                        dir="ltr"
                        className="mt-2 w-full px-3 py-1.5 rounded-lg border-2 border-gray-200 focus:border-gold outline-none bg-white text-xs font-mono"
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* ملاحظات المراجعة */}
              <div className="mt-3">
                <label className="block text-[11px] font-bold text-ink-muted mb-1">📝 ملاحظات المراجعة (اختياري)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={2}
                  placeholder="ملاحظات داخلية عن المراجعة…"
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gold outline-none bg-white text-xs"
                />
              </div>

              {/* 🔗 روابط مفيدة للتحقق — كل واحد لجهة مختلفة */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-3 text-[11px] text-blue-900">
                <div className="font-bold mb-1.5">🔗 روابط رسمية للتحقق:</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
                  <a href="https://eservicesredp.rega.gov.sa/auth/queries/Brokerage" target="_blank" rel="noreferrer"
                     className="block px-2.5 py-2 rounded bg-white border border-blue-200 hover:bg-blue-100 transition">
                    <div className="font-bold">🪪 الاستعلام عن وسيط عقاري</div>
                    <div className="text-[10px] text-ink-muted">التحقق من رخصة المعلن/الوسيط (فال)</div>
                  </a>
                  <a href="https://eservicesredp.rega.gov.sa/e-services/inquiry-about-the-real-estate-advertisement-license/form" target="_blank" rel="noreferrer"
                     className="block px-2.5 py-2 rounded bg-white border border-blue-200 hover:bg-blue-100 transition">
                    <div className="font-bold">🏢 الاستعلام عن ترخيص إعلان</div>
                    <div className="text-[10px] text-ink-muted">منصة الهيئة العامة للعقار للتحقق من رخصة الإعلان العقاري</div>
                  </a>
                  <a href="https://srem.moj.gov.sa/deed-inquiry" target="_blank" rel="noreferrer"
                     className="block px-2.5 py-2 rounded bg-white border border-blue-200 hover:bg-blue-100 transition">
                    <div className="font-bold">⚖️ الاستعلام عن صك</div>
                    <div className="text-[10px] text-ink-muted">منصة وزارة العدل للتحقق من صكوك الملكية</div>
                  </a>
                </div>
                <p className="mt-2 text-[10px] text-blue-700">
                  💡 <b>تنبيه:</b> رخصة <b>فال</b> ورخصة <b>الإعلان العقاري</b> جهتان منفصلتان وتحققهما يتم في موقعين مختلفين.
                </p>
              </div>
            </section>
          )}

          {/* 📋 سجل المراجعة (يظهر للعقارات المراجَعة سابقاً) */}
          {p.reviewedAt && p.reviewedBy && (
            <section className={`border-2 rounded-2xl p-4 ${
              p.status === 'ACTIVE' ? 'border-green-300 bg-green-50/50'
              : p.status === 'REJECTED' ? 'border-red-300 bg-red-50/50'
              : 'border-gray-200 bg-gray-50'
            }`}>
              <h3 className="text-sm font-black text-navy-dark mb-3 flex items-center gap-2 border-r-4 border-gold pr-2">
                📋 سجل المراجعة
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs mb-3">
                <ReviewField label="المراجع" value={p.reviewedBy.name} />
                <ReviewField label="تاريخ المراجعة" value={new Date(p.reviewedAt).toLocaleString('ar-SA')} />
                <ReviewField label="القرار" value={
                  p.status === 'ACTIVE' ? '✅ معتمد' :
                  p.status === 'REJECTED' ? '✕ مرفوض' :
                  STATUS_LABELS[p.status]?.label ?? p.status
                } highlight />
                {p.adLicenseNumber && (
                  <ReviewField label="رقم رخصة الإعلان" value={p.adLicenseNumber} dir="ltr" />
                )}
              </div>

              {/* خطوات المراجعة المنفّذة */}
              {p.reviewChecklist && (
                <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3">
                  <div className="text-[11px] font-bold text-ink-muted mb-2">✓ الخطوات المنفّذة:</div>
                  <div className="space-y-1 text-xs">
                    <ChecklistRow checked={!!p.reviewChecklist.property}        label="الاطلاع على العقار" />
                    <ChecklistRow checked={!!p.reviewChecklist.propertyDocs}    label="التحقق من وثائق العقار (الصك)" />
                    <ChecklistRow checked={!!p.reviewChecklist.advertiserAndFal} label="التحقق من المعلن ورخصة فال" />
                    <ChecklistRow checked={!!p.reviewChecklist.adLicense}        label="التحقق من رخصة الإعلان العقاري" />
                  </div>
                </div>
              )}

              {/* ملاحظات المراجع الداخلية */}
              {p.reviewNotes && (
                <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3">
                  <div className="text-[11px] font-bold text-ink-muted mb-1">📝 ملاحظات المراجع:</div>
                  <p className="text-xs whitespace-pre-line leading-relaxed">{p.reviewNotes}</p>
                </div>
              )}

              {/* سبب الرفض */}
              {p.status === 'REJECTED' && p.rejectionReason && (
                <div className="bg-red-50 border border-red-300 rounded-xl p-3 text-sm">
                  <div className="font-bold text-red-800 mb-1">✕ سبب الرفض:</div>
                  <p className="text-xs whitespace-pre-line text-red-700">{p.rejectionReason}</p>
                </div>
              )}
            </section>
          )}

          {/* قرار الرفض السابق (لو مرفوض بدون سجل مراجعة كامل) */}
          {p.status === 'REJECTED' && !p.reviewedAt && p.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
              <div className="font-bold mb-1">✕ سبب الرفض السابق:</div>
              <p className="whitespace-pre-line text-xs">{p.rejectionReason}</p>
            </div>
          )}
        </div>

        {/* تذييل — قرار */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl flex items-center justify-between flex-wrap gap-2 sticky bottom-0">
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
          {isPending ? (
            <div className="flex gap-2">
              <Button
                variant="primary"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={onReject}
              >
                ✕ رفض مع سبب
              </Button>
              <Button
                variant="primary"
                className={canApprove ? '!bg-green-600 hover:!bg-green-700' : ''}
                onClick={() => canApprove && approve.mutate()}
                disabled={!canApprove || approve.isPending}
                loading={approve.isPending}
              >
                {!allChecked
                  ? `✓ اعتماد (متبقي ${4 - checkedCount} خطوات)`
                  : !adLicenseNum.trim()
                  ? '✓ اعتماد (أدخل رقم الترخيص)'
                  : '✓ اعتماد ونشر'}
              </Button>
            </div>
          ) : (
            <span className="text-xs text-ink-muted">العقار {STATUS_LABELS[p.status]?.label} — لا حاجة للمراجعة الآن</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewField({ label, value, highlight, dir }: { label: string; value: string; highlight?: boolean; dir?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-2">
      <div className="text-[10px] text-ink-muted mb-0.5">{label}</div>
      <div className={`text-xs font-bold ${highlight ? 'text-gold-dark' : 'text-ink'}`} dir={dir}>{value}</div>
    </div>
  );
}

function ChecklistRow({ checked, label }: { checked: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded ${checked ? 'text-green-700' : 'text-gray-500'}`}>
      <span className={`inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold ${
        checked ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
      }`}>{checked ? '✓' : '○'}</span>
      <span>{label}</span>
    </div>
  );
}

function ReviewCheckbox({ step, checked, onChange, label, hint }: {
  step?: number;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className={`flex items-start gap-2 p-2.5 rounded-xl border cursor-pointer transition ${
      checked ? 'border-green-400 bg-green-50' : 'border-gold/30 bg-white hover:bg-gold-soft'
    }`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-green-600 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-bold flex items-center gap-1.5 ${checked ? 'text-green-800' : 'text-gold-dark'}`}>
          {step && (
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
              checked ? 'bg-green-600 text-white' : 'bg-gold/20 text-gold-dark'
            }`}>
              {checked ? '✓' : step}
            </span>
          )}
          <span>{label}</span>
        </div>
        <div className="text-[11px] text-ink-muted mt-0.5">{hint}</div>
      </div>
    </label>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مودال تحكم العقار ━━━━━━━━━━━━━━━━━━━━
/* ━━━━━━━━━━━━━━━━━━ تحكم كامل بالعقار (تعديل + حالة + صور + موقع + روابط) ━━━━━━━━━━━━━━━━━━ */
function PropertyControlModal({ property, onClose, onUpdated }: {
  property: PropertyRow;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [tab, setTab] = useState<'basic' | 'price' | 'location' | 'media' | 'status'>('basic');

  // 🎯 الحقول القابلة للتعديل (محمّلة من الـ row)
  const [form, setForm] = useState({
    type:        property.type,
    purpose:     property.purpose,
    title:       property.title,
    titleEn:     '',
    description: property.description ?? '',
    price:       String(property.price ?? 0),
    area:        String(property.area ?? 0),
    rooms:       String(property.rooms ?? 0),
    bathrooms:   String(property.bathrooms ?? 0),
    floors:      '0',
    ageYears:    '0',
    streetWidth: '0',
    facade:      '',
    city:        property.city,
    district:    property.district ?? '',
    address:     '',
    latitude:    '',
    longitude:   '',
    coverImage:  property.coverImage ?? '',
    videoUrl:    '',
    virtualTourUrl: '',
    deedNumber:  '',
  });

  // 🔄 جلب التفاصيل الكاملة (لإكمال الحقول الناقصة في الـ row)
  useQuery({
    queryKey: ['admin', 'property-detail', property.id],
    queryFn: async () => {
      const res = await api.get<{ data: Record<string, unknown> }>(`/api/properties/${property.id}`).catch(() => null);
      if (!res?.data) return null;
      const d = res.data as Record<string, string | number | null | undefined>;
      setForm((f) => ({
        ...f,
        titleEn:     String(d.titleEn ?? ''),
        floors:      String(d.floors ?? 0),
        ageYears:    String(d.ageYears ?? 0),
        streetWidth: String(d.streetWidth ?? 0),
        facade:      String(d.facade ?? ''),
        address:     String(d.address ?? ''),
        latitude:    d.latitude ? String(d.latitude) : '',
        longitude:   d.longitude ? String(d.longitude) : '',
        videoUrl:    String(d.videoUrl ?? ''),
        virtualTourUrl: String(d.virtualTourUrl ?? ''),
        deedNumber:  String(d.deedNumber ?? ''),
      }));
      return d;
    },
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // 💾 حفظ التعديلات
  const saveM = useMutation({
    mutationFn: () => api.put(`/api/properties/${property.id}`, {
      type: form.type,
      purpose: form.purpose,
      title: form.title,
      titleEn: form.titleEn || undefined,
      description: form.description || undefined,
      price: Number(form.price),
      area: Number(form.area),
      rooms: Number(form.rooms),
      bathrooms: Number(form.bathrooms),
      floors: Number(form.floors),
      ageYears: Number(form.ageYears),
      streetWidth: Number(form.streetWidth),
      facade: form.facade || undefined,
      city: form.city,
      district: form.district || undefined,
      address: form.address || undefined,
      latitude: form.latitude ? Number(form.latitude) : undefined,
      longitude: form.longitude ? Number(form.longitude) : undefined,
      coverImage: form.coverImage || undefined,
      videoUrl: form.videoUrl || undefined,
      virtualTourUrl: form.virtualTourUrl || undefined,
      deedNumber: form.deedNumber || undefined,
    }),
    onSuccess: () => { onUpdated(); },
  });

  // 🚦 تغيير الحالة + التمييز
  const setStatus = useMutation({
    mutationFn: (status: string) => api.put(`/api/properties/${property.id}/status`, { status }),
    onSuccess: onUpdated,
  });
  const toggleFeatured = useMutation({
    mutationFn: () => api.put(`/api/properties/${property.id}/featured`, { featured: !property.featured }),
    onSuccess: onUpdated,
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        {/* رأس */}
        <div className="bg-gradient-to-l from-navy-darker to-navy-dark text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-black text-lg truncate">⚙️ تحكم كامل: {property.title}</h2>
            <p className="text-xs text-white/70">#{property.id.slice(-8).toUpperCase()} · {property.owner?.name ?? 'بدون مالك'}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl shrink-0">✕</button>
        </div>

        {/* تابات */}
        <div className="border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
          <SubTab active={tab === 'basic'}    onClick={() => setTab('basic')}>📋 معلومات أساسية</SubTab>
          <SubTab active={tab === 'price'}    onClick={() => setTab('price')}>💰 سعر ومساحة</SubTab>
          <SubTab active={tab === 'location'} onClick={() => setTab('location')}>📍 الموقع</SubTab>
          <SubTab active={tab === 'media'}    onClick={() => setTab('media')}>🖼️ الوسائط</SubTab>
          <SubTab active={tab === 'status'}   onClick={() => setTab('status')}>🚦 الحالة والتمييز</SubTab>
        </div>

        <div className="p-6 space-y-4">
          {/* 1) معلومات أساسية */}
          {tab === 'basic' && (
            <div className="space-y-4">
              <Input label="العنوان (عربي) *" value={form.title} onChange={set('title')} />
              <Input label="العنوان (إنجليزي)" value={form.titleEn} onChange={set('titleEn')} dir="ltr" />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">الوصف</label>
                <textarea
                  value={form.description}
                  onChange={set('description')}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">النوع</label>
                  <select value={form.type} onChange={set('type')} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
                    {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    <option value="BUILDING">عمارة</option>
                    <option value="FARM">مزرعة</option>
                    <option value="WAREHOUSE">مستودع</option>
                    <option value="REST_HOUSE">استراحة</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">الغرض</label>
                  <select value={form.purpose} onChange={set('purpose')} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
                    <option value="SALE">للبيع</option>
                    <option value="RENT">للإيجار</option>
                    <option value="INVESTMENT">للاستثمار</option>
                    <option value="AUCTION">للمزاد</option>
                  </select>
                </div>
              </div>
              <Input label="رقم الصك" value={form.deedNumber} onChange={set('deedNumber')} dir="ltr" />
            </div>
          )}

          {/* 2) سعر ومساحة */}
          {tab === 'price' && (
            <div className="grid grid-cols-2 gap-3">
              <Input label="السعر (ر.س) *" type="number" value={form.price} onChange={set('price')} />
              <Input label="المساحة (م²) *" type="number" value={form.area} onChange={set('area')} />
              <Input label="عدد الغرف" type="number" value={form.rooms} onChange={set('rooms')} />
              <Input label="عدد الحمامات" type="number" value={form.bathrooms} onChange={set('bathrooms')} />
              <Input label="عدد الأدوار" type="number" value={form.floors} onChange={set('floors')} />
              <Input label="عمر العقار (سنوات)" type="number" value={form.ageYears} onChange={set('ageYears')} />
              <Input label="عرض الشارع (م)" type="number" value={form.streetWidth} onChange={set('streetWidth')} />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">الواجهة</label>
                <select value={form.facade} onChange={set('facade')} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
                  <option value="">—</option>
                  <option value="north">شمالية</option>
                  <option value="south">جنوبية</option>
                  <option value="east">شرقية</option>
                  <option value="west">غربية</option>
                  <option value="ne">شمالية شرقية</option>
                  <option value="nw">شمالية غربية</option>
                  <option value="se">جنوبية شرقية</option>
                  <option value="sw">جنوبية غربية</option>
                </select>
              </div>
            </div>
          )}

          {/* 3) الموقع */}
          {tab === 'location' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">المدينة *</label>
                  <select value={form.city} onChange={set('city')} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
                    {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <Input label="الحي" value={form.district} onChange={set('district')} />
              </div>
              <Input label="العنوان التفصيلي" value={form.address} onChange={set('address')} />

              {/* 🗺️ الخريطة لتحديد الموقع — تستخرج المدينة والحي تلقائياً */}
              <LocationPicker
                label="📍 موقع العقار على الخريطة"
                city={form.city}
                value={{
                  lat: form.latitude ? Number(form.latitude) : null,
                  lng: form.longitude ? Number(form.longitude) : null,
                  address: form.address,
                }}
                onChange={(v) => {
                  setForm((s) => ({
                    ...s,
                    latitude: String(v.lat),
                    longitude: String(v.lng),
                    address: v.address ?? s.address,
                    city: v.detectedCity ?? s.city,
                    district: v.detectedDistrict ?? s.district,
                  }));
                }}
                height={320}
              />
            </div>
          )}

          {/* 4) الوسائط */}
          {tab === 'media' && (
            <div className="space-y-3">
              <Input label="الصورة الرئيسية (URL)" value={form.coverImage} onChange={set('coverImage')} dir="ltr" />
              {form.coverImage && (
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <img src={form.coverImage} alt="" className="w-full h-48 object-cover" onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.3')} />
                </div>
              )}
              <Input label="رابط الفيديو" value={form.videoUrl} onChange={set('videoUrl')} dir="ltr" hint="YouTube/Vimeo أو رابط mp4 مباشر" />
              <Input label="الجولة الافتراضية 360°" value={form.virtualTourUrl} onChange={set('virtualTourUrl')} dir="ltr" />
            </div>
          )}

          {/* 5) الحالة والتمييز */}
          {tab === 'status' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-black text-ink-muted mb-2">🎛️ الحالة:</h3>
                <div className="flex flex-wrap gap-2">
                  {(['ACTIVE', 'PENDING_REVIEW', 'REJECTED', 'SOLD', 'RENTED', 'ARCHIVED'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus.mutate(s)}
                      disabled={property.status === s || setStatus.isPending}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
                        property.status === s
                          ? 'bg-navy-dark text-white border-navy-dark'
                          : 'bg-white border-gray-200 hover:border-navy text-ink-secondary'
                      }`}
                    >
                      {STATUS_LABELS[s]?.icon} {STATUS_LABELS[s]?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gold-soft/40 border border-gold/30 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-navy-dark text-sm">⭐ تمييز العقار</div>
                    <div className="text-[11px] text-ink-muted">يظهر في "العقارات المميّزة" بالصفحة الرئيسية</div>
                  </div>
                  <button
                    onClick={() => toggleFeatured.mutate()}
                    disabled={toggleFeatured.isPending}
                    className={`px-4 py-2 rounded-lg text-sm font-bold border-2 transition ${
                      property.featured
                        ? 'bg-gold text-white border-gold'
                        : 'bg-white border-gold/40 text-gold-dark hover:bg-gold/10'
                    }`}
                  >
                    {property.featured ? '⭐ مميّز' : '☆ غير مميّز'}
                  </button>
                </div>
              </div>

              {/* روابط سريعة */}
              <div>
                <h3 className="text-sm font-black text-ink-muted mb-2">🔗 روابط:</h3>
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/properties/${property.id}`} target="_blank" className="px-3 py-1.5 rounded-lg bg-navy-soft text-navy-dark text-xs font-bold hover:bg-navy/10">
                    👁️ افتح في الموقع
                  </Link>
                  {property.owner?.id && (
                    <Link to={`/admin/users?id=${property.owner.id}`} className="px-3 py-1.5 rounded-lg bg-gold/15 text-gold-dark text-xs font-bold hover:bg-gold/25">
                      👤 ملف المالك
                    </Link>
                  )}
                </div>
              </div>

              {/* بيانات المالك */}
              {property.owner && (
                <div>
                  <h3 className="text-sm font-black text-ink-muted mb-2">👤 بيانات المالك:</h3>
                  <div className="bg-gray-50 rounded-xl p-3 text-sm">
                    <div className="font-bold text-navy-dark">{property.owner.name}</div>
                    {property.owner.phone && (
                      <a href={`tel:${property.owner.phone}`} className="text-xs text-navy hover:underline" dir="ltr">📞 {property.owner.phone}</a>
                    )}
                    {property.owner.email && (
                      <div className="text-xs text-ink-muted" dir="ltr">📧 {property.owner.email}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* رسائل الحفظ */}
          {saveM.isSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-800">
              ✓ تم حفظ التعديلات بنجاح
            </div>
          )}
          {saveM.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-800">
              ⚠️ {(saveM.error as Error)?.message ?? 'فشل الحفظ'}
            </div>
          )}
        </div>

        {/* فوتر مع زر الحفظ */}
        <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 rounded-b-2xl flex justify-between gap-2 flex-wrap">
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
          {tab !== 'status' && (
            <Button variant="primary" onClick={() => saveM.mutate()} loading={saveM.isPending}>
              💾 حفظ التعديلات
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* تاب فرعي داخل المودال */
function SubTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 text-xs font-bold whitespace-nowrap border-b-2 transition ${
        active ? 'border-navy text-navy-dark' : 'border-transparent text-ink-muted hover:text-navy-dark hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━
function QuickStat({ label, count, color, icon, active, onClick, pulse }: {
  label: string; count: number; color: 'navy' | 'amber' | 'green' | 'red' | 'gray';
  icon: string; active?: boolean; onClick: () => void; pulse?: boolean;
}) {
  const colors = {
    navy:  { active: 'border-navy bg-navy-soft',     text: 'text-navy-dark' },
    amber: { active: 'border-amber-400 bg-amber-50', text: 'text-amber-700' },
    green: { active: 'border-green-400 bg-green-50', text: 'text-green-700' },
    red:   { active: 'border-red-400 bg-red-50',     text: 'text-red-700' },
    gray:  { active: 'border-gray-400 bg-gray-100',  text: 'text-gray-700' },
  }[color];
  return (
    <button
      onClick={onClick}
      className={`text-right p-3 rounded-2xl border-2 bg-white transition hover:shadow-brand ${
        active ? colors.active : 'border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xl">{icon}</span>
        {pulse && count > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
      </div>
      <div className={`text-2xl font-black mt-1 ${colors.text}`}>{count}</div>
      <div className="text-[11px] text-ink-muted">{label}</div>
    </button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase whitespace-nowrap">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
